<template>
  <StSkeletonLoader
    v-if="loading || !isReady"
    class="skeleton-element"
    height="250px"
    bg-color="var(--background-primary)"
    :shimmer-opacity="0.28"
  >
    <div class="skeleton-header" />
    <div class="skeleton-content" />
  </StSkeletonLoader>
  <ClientOnly v-else>
    <div
      class="vip-club-header"
      data-t="vip-club-header"
      :class="vipClubHeaderClasses"
      :style="headerBackgroundColorStyles"
    >
      <slot name="head">
        <h1 class="title" data-t="vip-club-title">{{ t('vip.title') }}</h1>
        <p class="subtitle" data-t="vip-club-subtitle">
          {{ t('vip.subtitle') }}
        </p>
      </slot>
      <StButton
        v-if="!isAuthenticated"
        class="button"
        type="primary"
        :size="isMobile ? 'l' : 'xl'"
        data-t="vip-club-register-button"
        :to="{ query: { modal: 'register' } }"
      >
        {{ t('vip.registerAndGetBonuses') }}
      </StButton>
      <div
        v-else-if="!hasZeroBets"
        class="progress-block"
        data-t="vip-club-progress-block"
      >
        <div class="levels" data-t="vip-club-levels">
          <div class="level" data-t="vip-club-current-level">
            <img
              v-if="levelIcon"
              :src="levelIcon"
              class="level-icon"
              alt="icon"
              data-t="vip-club-level-icon"
            />
            <span class="level-label" data-t="vip-club-level-label">
              {{ userLevelTitle }}
            </span>
          </div>
          <img :src="arrows" alt=">>>" data-t="vip-club-arrows" />
          <div class="level" data-t="vip-club-next-level">
            <img
              :src="nextLevelIcon"
              class="level-icon"
              alt="icon"
              data-t="vip-club-next-level-icon"
            />
            <span class="level-label" data-t="vip-club-next-level-label">
              {{ nextLevelTitle }}
            </span>
          </div>
        </div>
        <div class="progress-bar" data-t="vip-club-progress-bar">
          <div class="progress-current" />
        </div>
        <div class="total-bets" data-t="vip-club-total-bets">
          <span data-t="vip-club-total-bets-label">{{
            t('vip.totalBets')
          }}</span>
          <span data-t="vip-club-total-bets-value">
            {{ progressInCurrency }}
          </span>
        </div>
      </div>
      <div v-else class="no-bets-block-wrapper">
        <div class="no-bets-block">
          <span class="no-bets-text">
            {{ t('vip.noBetsBlock', { amount: betsToJoinVipClub }) }}
          </span>
          <StButton class="no-bets-button" size="l" to="/casino">
            {{ t('vip.placeBets') }}
          </StButton>
        </div>
      </div>
    </div>
  </ClientOnly>
</template>

<script setup lang="ts">
import arrows from './parts/arrows.svg'
import { useVipClub } from '../../composables/useVipClub'
import { useVipClubDeps } from '../../useDeps'

const { type } = defineProps<{
  type?: string
}>()

const { t } = useI18n()
const { isAuthenticated, isReady } = useVipClubDeps()
const { isMobile } = usePlatform()
const { setHeaderColor } = useMobileLayout()
const {
  userLevelTitle,
  nextLevelTitle,
  levelIcon,
  nextLevelIcon,
  progressInCurrency,
  progressPercent,
  backgroundColor,
  backgroundMobileColor,
  hasZeroBets,
  betsToJoinVipClub,
  loading,
} = useVipClub()

const headerBackgroundColorStyles = computed(() => {
  if (isMobile.value) {
    return isAuthenticated.value
      ? { background: backgroundMobileColor.value }
      : {}
  }

  return isAuthenticated.value ? { background: backgroundColor.value } : {}
})

const vipClubHeaderClasses = computed(() => [
  {
    mobile: isMobile.value,
    authorized: isAuthenticated.value,
    'has-zero-bets': hasZeroBets.value && isAuthenticated.value,
  },
  type,
])

watchEffect(() => {
  if (isMobile.value) {
    setHeaderColor(backgroundMobileColor.value)
  }
})
</script>

<style scoped>
.vip-club-header {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-125);

  width: 100%;
  padding: var(--spacing-400);

  background-image: url('./parts/vip_hero_desktop.webp');
  background-size: cover;
  border-radius: var(--border-radius-150);

  .title {
    margin: 0;
    font: var(--desktop-text-3xl-semibold);
  }

  .subtitle {
    max-width: 560px;
    margin: 0;
    margin-bottom: var(--spacing-200);

    font: var(--desktop-text-lg-medium);
    color: var(--text-secondary);
  }

  .button {
    width: fit-content;
    margin-top: var(--spacing-400);
  }

  .level-icon {
    width: 36px;
    height: 36px;
    filter: drop-shadow(0 8px 12px rgb(0 0 0 / 24%));
  }
}

.vip-club-header.authorized {
  gap: var(--spacing-125);
  padding: var(--spacing-300) var(--spacing-200);
  background-image: none;

  .progress-block {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-100);
  }

  .levels {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: var(--spacing-025);
  }

  .level {
    display: inline-flex;
    gap: var(--spacing-075);
    align-items: center;
  }

  .level-label {
    font: var(--desktop-text-2lg-semibold);
    text-transform: capitalize;
  }

  .progress-bar {
    overflow: hidden;
    height: var(--spacing-100);
    background: var(--background-ghost-tertiary);
    border-radius: var(--border-radius-full);
  }

  .progress-current {
    width: v-bind(progressPercent);
    height: 8px;
    background: var(--background-ghost-primary);
    border-radius: var(--border-radius-full);
  }

  .total-bets {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font: var(--desktop-text-sm-medium);
  }

  .subtitle {
    max-width: 100%;
  }

  &.simple {
    background-image: none;
  }
}

.vip-club-header.mobile {
  align-items: center;
  height: 316px;
  padding: var(--spacing-200);
  background-image: url('./parts/vip_hero_mobile.webp');

  .title {
    font: var(--mobile-title-1-semibold);
  }

  .subtitle {
    font: var(--mobile-text-content-regular);
    text-align: center;
  }

  .button {
    width: 100%;
    margin-top: auto;
  }

  .level-icon {
    width: 36px;
    height: 36px;
  }
}

.vip-club-header.mobile.authorized {
  gap: var(--spacing-075);
  align-items: flex-start;

  height: auto;
  padding: var(--spacing-200);
  padding-bottom: var(--spacing-400);

  background-image: none;
  border-radius: 0;

  .title {
    font: var(--desktop-text-xl-semibold);
  }

  .subtitle {
    text-align: start;
  }

  .level-label {
    font: var(--mobile-title-2-semibold);
  }

  .total-bets {
    font: var(--mobile-caption-1-regular);
  }

  .progress-block {
    width: 100%;
  }
}

.vip-club-header.has-zero-bets {
  padding: var(--spacing-250) var(--spacing-300);
  background-color: var(--background-secondary);
  background-image: linear-gradient(
    180deg,
    var(--background-secondary, #2d2d39) 40%,
    var(--background-base, #111118) 80%
  );
  /* stylelint-disable-next-line */
  .subtitle {
    font: var(--desktop-text-md-medium);
  }

  .no-bets-block-wrapper {
    width: 100%;
  }

  .no-bets-block {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-200);
    align-items: center;

    padding: var(--spacing-300);

    background-color: var(--background-base);
    border-radius: var(--border-radius-150);
  }

  .no-bets-text {
    font: var(--desktop-text-lg-semibold);
    text-align: center;
  }

  .no-bets-button {
    width: max-content;
    min-width: 140px;
  }

  &.mobile {
    background-color: var(--background-secondary);

    .no-bets-block {
      padding: var(--spacing-150);
    }

    .no-bets-text {
      font: var(--mobile-headline-semibold);
    }

    .no-bets-button {
      width: 100%;
    }

    .subtitle {
      font: var(--mobile-text-content-regular);
    }
  }
}

.skeleton-element {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-100);
  align-items: flex-start;

  padding: var(--spacing-200);

  background-color: var(--background-primary);
  border-radius: var(--border-radius-150);
}

.skeleton-header {
  width: 40%;
  height: 36px;
  margin-bottom: var(--spacing-075);

  background-color: var(--background-secondary);
  border-radius: var(--border-radius-100);
}

.skeleton-content {
  width: 70%;
  height: 100px;
  background-color: var(--background-secondary);
  border-radius: var(--border-radius-100);
}
</style>
